"use client";

import Link from "next/link";
import { displayCredits, formatCredits, storageCredits, DISPLAY_CREDITS_PER_UNIT } from "@/lib/ai-credits/monthly-policy";
import { useEffect, useMemo, useState } from "react";
import { useAiCreditPaymentNotice } from "@/hooks/use-ai-credit-payment-notice";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { ArrowLeft, Coins, Loader2, Sparkles } from "lucide-react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "@/components/checkout/checkout-form";
import { CREDITS_PER_COVER_IMAGE, CREDITS_PER_REDRAFT, type AiCreditPricingConfig } from "@/lib/ai-credits/pricing";
import { useTheme } from "@/components/theme/theme-provider";
import { getStripeAppearance } from "@/lib/stripe-appearance";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";

// Call loadStripe once outside the component so the Stripe object isn't
// recreated on every render.
const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY!);
interface AiCreditPaywallProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Serialized server pricing (per-credit cents, bounds, quick picks). */
  pricing: AiCreditPricingConfig | null;
  /** Credits missing for the blocked action (from the 402 payload). */
  shortfall: number | null;
  /** Same-site path Stripe returns to after checkout. */
  returnPath: string;
  /** Initial pack in accounting units, when opened from the credit store. */
  initialCredits?: number;
}

/**
 * Buy-credits paywall: the author picks how many credits to buy and pays
 * once (a single one-time Stripe Checkout — never a subscription). Shown
 * when a billable image action can't be covered by the story's free tier plus
 * the paid balance, or proactively from the balance badge.
 */
export function AiCreditPaywall({
  open,
  onOpenChange,
  pricing,
  shortfall,
  returnPath,
  initialCredits = 2000
}: AiCreditPaywallProps) {
  const {
    resolvedTheme
  } = useTheme();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  useAiCreditPaymentNotice(searchParams.get("credits_success") === "1" && searchParams.get("redirect_status") === "succeeded" ? searchParams.get("payment_intent") : null);
  const [quantityInput, setQuantityInput] = useState<string>(() => String(displayCredits(initialCredits)));
  const [buying, setBuying] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Once a PaymentIntent is created, we swap the quantity picker for the
  // embedded Elements card form (same in-app flow as coin packs).
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [payQuantity, setPayQuantity] = useState(0);
  const [payPriceStr, setPayPriceStr] = useState<string | null>(null);
  const quantity = storageCredits(Number(quantityInput)) ?? NaN;

  // Reset back to the quantity step whenever the dialog is closed.
  useEffect(() => {
    if (!open) {
      setClientSecret(null);
      setPayPriceStr(null);
      setError(null);
    }
  }, [open]);

  // Stripe redirects append intent state to the return URL. Remove it from
  // every image workspace once it has been observed so a later checkout
  // cannot read the previous intent and display stale success messaging.
  useEffect(() => {
    const stale = ["credits_success", "payment_intent", "payment_intent_client_secret", "redirect_status", "setup_intent", "setup_intent_client_secret"];
    if (searchParams.get("checkout") === "complete") stale.push("checkout");
    if (!stale.some(key => searchParams.has(key))) return;
    const next = new URLSearchParams(searchParams);
    stale.forEach(key_0 => next.delete(key_0));
    const query = next.toString();
    router.replace(query ? `${pathname}?${query}` : pathname, {
      scroll: false
    });
  }, [pathname, router, searchParams]);
  const validation = useMemo(() => {
    if (!pricing) return {
      valid: false as const,
      message: null
    };
    if (!Number.isInteger(quantity)) {
      return {
        valid: false as const,
        message: "Enter credits in increments of 50."
      };
    }
    if (quantity < pricing.min) {
      return {
        valid: false as const,
        message: `Minimum: ${formatCredits(pricing.min)} credits.`
      };
    }
    if (quantity > pricing.max) {
      return {
        valid: false as const,
        message: `Maximum: ${formatCredits(pricing.max)} credits.`
      };
    }
    return {
      valid: true as const,
      message: null
    };
  }, [pricing, quantity]);
  const priceStr = pricing && validation.valid ?
  // Match the server's whole-cent rounding (rate may be fractional).
  `$${(Math.round(quantity * pricing.centsPerCredit) / 100).toFixed(2)}` : null;

  // Mint a PaymentIntent for the chosen quantity, then reveal the embedded
  // card form. The server (create-payment-intent) is authoritative on price.
  const handleContinue = async () => {
    if (buying || !validation.valid) return;
    setBuying(true);
    setError(null);
    try {
      const resp = await fetch("/api/create-payment-intent", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          type: "ai_credits",
          credits: quantity
        })
      });
      const data = (await resp.json()) as {
        clientSecret?: string;
        priceStr?: string;
        error?: string;
      };
      if (!resp.ok || !data.clientSecret) {
        setError(data.error ?? "Couldn’t start checkout. Try again.");
        return;
      }
      setPayQuantity(quantity);
      setPayPriceStr(data.priceStr ?? priceStr);
      setClientSecret(data.clientSecret);
    } catch {
      setError("Couldn’t start checkout. Try again.");
    } finally {
      setBuying(false);
    }
  };

  // Stripe redirects back here on completion; the image workspace picks up the
  // flag to refresh the balance.
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const returnUrl = `${origin}${returnPath}${returnPath.includes("?") ? "&" : "?"}credits_success=1`;
  const elementsAppearance = getStripeAppearance(resolvedTheme);
  return <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-md max-h-[90dvh] overflow-y-auto border-border/50 dark:border-white/10 bg-card/95 backdrop-blur-xl">
                <div className="space-y-1">
                    <DialogTitle className="flex items-center gap-2.5 text-base font-heading font-bold text-foreground">
                        <span className="p-2 rounded-lg bg-gradient-to-br from-brand to-brand-light text-white shadow-lg shadow-brand/30">
                            <Coins className="size-4" />
                        </span>
                        Buy AI credits
                    </DialogTitle>
                    <DialogDescription className="text-sm text-muted-foreground">
                        {shortfall && shortfall > 0 ? `You need ${formatCredits(shortfall)} more credits. ` : ""}
                        One-time purchase. Credits never expire.
                    </DialogDescription>
                </div>
                <p className="text-xs text-muted-foreground">
                    Images: {formatCredits(CREDITS_PER_COVER_IMAGE)} credits · Prompt redrafts: {formatCredits(CREDITS_PER_REDRAFT)} credits
                </p>

                {!pricing ? <p className="rounded-lg border border-border/50 dark:border-white/10 bg-muted/40 px-4 py-6 text-sm text-muted-foreground">
                        Credit purchases are unavailable. Try again later.
                    </p> : clientSecret ? <div className="space-y-4">
                        <button type="button" onClick={() => {
          setClientSecret(null);
          setPayPriceStr(null);
        }} className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors">
                            <ArrowLeft className="size-3.5" />
                            Change amount
                        </button>
                        <p className="text-sm font-medium">{formatCredits(payQuantity)} credits · {payPriceStr} one-time</p>
                        <Elements stripe={stripePromise} options={{
          clientSecret,
          appearance: elementsAppearance
        }}>
                            <CheckoutForm compact amountStr={payPriceStr ?? priceStr ?? ""} returnUrl={returnUrl} />
                        </Elements>
                    </div> : <div className="space-y-4">
                        {/* Quick-pick chips */}
                        <div className="flex flex-wrap gap-2">
                            {pricing.suggested.map(amount => {
            const active = quantity === amount;
            return <button key={amount} type="button" disabled={buying} onClick={() => setQuantityInput(String(displayCredits(amount)))} aria-pressed={active} className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all duration-150 disabled:opacity-50 ${active ? "bg-brand text-white border-brand shadow-lg shadow-brand/30" : "bg-background/60 text-muted-foreground border-border/60 dark:border-white/10 hover:border-primary/50 hover:text-foreground"}`}>
                                        {formatCredits(amount)} credits
                                    </button>;
          })}
                        </div>

                        {/* Custom quantity + live price */}
                        <div className="flex items-center gap-3">
                            <div className="flex-1">
                                <label htmlFor="ai-credit-quantity" className="mb-1.5 block text-xs font-semibold text-foreground">
                                    Credits
                                </label>
                                <Input id="ai-credit-quantity" type="number" inputMode="numeric" min={displayCredits(pricing.min)} max={displayCredits(pricing.max)} step={DISPLAY_CREDITS_PER_UNIT} value={quantityInput} disabled={buying} onChange={e => setQuantityInput(e.target.value)} className="bg-background/60" />
                            </div>
                            <div className="pt-5 text-right">
                                <p className="text-xs text-muted-foreground">
                                    {formatCredits(100 / pricing.centsPerCredit)} credits / $1
                                </p>
                                <p className="text-lg font-heading font-extrabold text-foreground">
                                    {priceStr ?? "—"}
                                </p>
                            </div>
                        </div>

                        {validation.message && <p className="text-xs text-destructive" role="alert">
                                {validation.message}
                            </p>}

                        <Button type="button" disabled={buying || !validation.valid} onClick={() => void handleContinue()} className="w-full rounded-full bg-brand hover:bg-brand/90 text-white shadow-lg shadow-brand/30">
                            {buying ? <Loader2 className="size-4 animate-spin" /> : <Sparkles className="size-4" />}
                            {validation.valid && priceStr ? `Continue — ${priceStr}` : "Continue to payment"}
                        </Button>
                        {pricing.monthlyPlansEnabled && shortfall != null && shortfall > 0 && !buying && <Button variant="link" asChild className="w-full">
                                <Link href="/credit-pricing" onClick={() => onOpenChange(false)}>
                                    View monthly plans
                                </Link>
                            </Button>}
                    </div>}

                {error && <p className="text-sm text-destructive" role="alert">
                        {error}
                    </p>}

            </DialogContent>
        </Dialog>;
}